import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    Checkbox, TextField, InputAdornment, Badge, IconButton, Collapse, Link, Tooltip
} from '@mui/material';
import {
    Search as SearchIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Close as CloseIcon, OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import {PLACE_TYPES, PLACE_TYPE_CATEGORIES} from '../models/PlaceType';

const PlaceTypesDialog = ({
    open,
    onClose,
    selectedPlaceTypes,
    onPlaceTypeChange
}) => {
    const [searchFilter, setSearchFilter] = useState('');
    const [expandedCategories, setExpandedCategories] = useState(['settlement']); // Default expand settlements

    const handleTypeToggle = (typeId) => {
        const newSelection = selectedPlaceTypes.includes(typeId)
            ? selectedPlaceTypes.filter(id => id !== typeId)
            : [...selectedPlaceTypes, typeId];
        onPlaceTypeChange(newSelection);
    };

    const handleCategoryToggle = (categoryKey, category) => {
        const categoryTypes = category.types.map(type => type.id);
        const allSelected = categoryTypes.every(typeId => selectedPlaceTypes.includes(typeId));
        
        if (allSelected) {
            // Deselect all types in this category
            const newSelection = selectedPlaceTypes.filter(typeId => !categoryTypes.includes(typeId));
            onPlaceTypeChange(newSelection);
        } else {
            // Select all types in this category
            const newSelection = [...new Set([...selectedPlaceTypes, ...categoryTypes])];
            onPlaceTypeChange(newSelection);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pb: 1
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Select Geographic Features
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {/* Search Filter */}
                <TextField
                    size="small"
                    placeholder="Search features..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    sx={{ mb: 2, width: '100%' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: '1rem' }} />
                            </InputAdornment>
                        )
                    }}
                />

                {/* Selection Summary */}
                <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    backgroundColor: 'rgba(59, 130, 246, 0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Selected: {selectedPlaceTypes.length} feature{selectedPlaceTypes.length !== 1 ? 's' : ''}
                    </Typography>
                    {selectedPlaceTypes.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectedPlaceTypes.slice(0, 8).map((typeId) => {
                                const type = PLACE_TYPES.find(t => t.id === typeId);
                                return (
                                    <Typography 
                                        key={typeId}
                                        variant="caption" 
                                        sx={{ 
                                            fontSize: '0.7rem', 
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            padding: '2px 6px', 
                                            borderRadius: 1,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <span>{type?.icon}</span>
                                        {type?.name || typeId}
                                    </Typography>
                                );
                            })}
                            {selectedPlaceTypes.length > 8 && (
                                <Typography variant="caption" sx={{ 
                                    fontSize: '0.7rem', 
                                    color: 'text.secondary',
                                    alignSelf: 'center'
                                }}>
                                    +{selectedPlaceTypes.length - 8} more
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Categorized Place Types */}
                <Box sx={{
                    maxHeight: '400px',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    backgroundColor: 'grey.50'
                }}>
                    {Object.entries(PLACE_TYPE_CATEGORIES).map(([categoryKey, category]) => {
                        const filteredTypes = category.types.filter(type => 
                            !searchFilter || 
                            type.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                            type.description.toLowerCase().includes(searchFilter.toLowerCase())
                        );
                        
                        if (filteredTypes.length === 0) return null;
                        
                        const isExpanded = expandedCategories.includes(categoryKey);
                        const selectedInCategory = filteredTypes.filter(type => selectedPlaceTypes.includes(type.id)).length;
                        const allSelected = filteredTypes.length > 0 && filteredTypes.every(type => selectedPlaceTypes.includes(type.id));
                        
                        return (
                            <Box key={categoryKey}>
                                {/* Category Header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1.5,
                                        backgroundColor: 'background.paper',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            backgroundColor: 'rgba(59, 130, 246, 0.05)'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={selectedInCategory > 0 && !allSelected}
                                            onChange={() => handleCategoryToggle(categoryKey, category)}
                                            size="small"
                                        />
                                        <span style={{ fontSize: '1rem' }}>{category.icon}</span>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {category.name}
                                        </Typography>
                                        {selectedInCategory > 0 && (
                                            <Badge 
                                                badgeContent={selectedInCategory} 
                                                color="primary" 
                                                sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: '16px', height: '16px' } }}
                                            />
                                        )}
                                    </Box>
                                    <IconButton 
                                        size="small" 
                                        sx={{ p: 0 }}
                                        onClick={() => {
                                            setExpandedCategories(prev => 
                                                isExpanded 
                                                    ? prev.filter(cat => cat !== categoryKey)
                                                    : [...prev, categoryKey]
                                            );
                                        }}
                                    >
                                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </IconButton>
                                </Box>

                                {/* Category Items */}
                                <Collapse in={isExpanded}>
                                    <Box sx={{ p: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1, display: 'block' }}>
                                            {category.description}
                                        </Typography>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                            gap: 1
                                        }}>
                                            {filteredTypes.map((type) => (
                                                <Box
                                                    key={type.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        border: '1px solid transparent',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                                                            borderColor: 'primary.main'
                                                        },
                                                        ...(selectedPlaceTypes.includes(type.id) && {
                                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                            borderColor: 'primary.main'
                                                        }),
                                                        transition: 'all 0.2s ease-in-out'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{ display: 'flex', alignItems: 'center', flex: 1 }}
                                                        onClick={() => handleTypeToggle(type.id)}
                                                    >
                                                        <Checkbox
                                                            checked={selectedPlaceTypes.includes(type.id)}
                                                            size="small"
                                                            sx={{ p: 0, mr: 1 }}
                                                        />
                                                        <span style={{ fontSize: '0.9rem', marginRight: '6px' }}>{type.icon}</span>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize: '0.8rem',
                                                                    lineHeight: 1.2,
                                                                    fontWeight: selectedPlaceTypes.includes(type.id) ? 600 : 400
                                                                }}
                                                            >
                                                                {type.name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ fontSize: '0.7rem', display: 'block', lineHeight: 1.1 }}
                                                            >
                                                                {type.description}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Tooltip title={`View ${type.name} on Wikidata`} arrow>
                                                        <Link
                                                            href={`https://www.wikidata.org/wiki/${type.wikidataId}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: 'text.secondary',
                                                                textDecoration: 'none',
                                                                padding: '4px',
                                                                borderRadius: 1,
                                                                ml: 1,
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                                }
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                                                        </Link>
                                                    </Tooltip>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={onClose} variant="contained">
                    Apply Selection ({selectedPlaceTypes.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PlaceTypesDialog;